import { useContext, useEffect, useState } from 'react'
import { DatePicker, Select, Table, Row, Col, message, Checkbox, Switch, Space, notification } from 'antd'
import moment from 'moment';
import dayjs from "dayjs/esm";
import weekday from "dayjs/plugin/weekday";
import localeData from "dayjs/plugin/localeData";

import './styles.css'

import { Context } from '../../context'
import { Fetch } from '../../fetch'
import { convertIdToKey, handleExportClick } from '../../utils'
import { CheckCircleFilled, CloseCircleOutlined, CloudDownloadOutlined } from '@ant-design/icons'

dayjs.extend(weekday);
dayjs.extend(localeData);

const { Column } = Table
const displayDateFormat = 'YYYY-MM-DD HH:mm:ss';

const ManageOrders = () => {
    const { token, apiUrl } = useContext(Context)
    const [orders, setOrders] = useState([])
    const [loading, setLoading] = useState(false)
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [filters, setFilters] = useState({
        start_date: dayjs().subtract(0, "month").startOf("month").format(displayDateFormat),
        end_date: dayjs().subtract(0, "month").endOf("month").format(displayDateFormat),
        plan: null,
        paid: true,
        currency: null,
    });
    const [plans, setPlans] = useState([]);
    const [applePlans, setApplePlans] = useState([]);
    const [androidPlans, setAndroidPlans] = useState([]);
    const [allPlans, setAllPlans] = useState([]);

    const handleTableDataChange = (page) => {
        let f = filters;
        let planstring = f.plan ? `plan=${f.plan}` : ``;
        let starting_date = filters.start_date;
        let ending_date = filters.end_date;
        let sdate = moment(starting_date).isValid() ? moment(starting_date).format(displayDateFormat) : null
        let edate = moment(ending_date).isValid() ? moment(ending_date).format(displayDateFormat) : null
        let datestring = sdate && edate ? `start_date=${sdate}&end_date=${edate}` : ``;
        let paidstring = f.paid ? `paid=${true}` : `paid=${false}`;
        let currencystring = f.currency ? `currency=${f.currency}` : ``;
        let fstring = `${planstring}&${datestring}&${paidstring}&${currencystring}`

        Fetch(
            `${apiUrl}/orders?current=${page.current}&size=${page.pageSize}&${fstring}`,
            token,
            (result) => {
                const orders = convertIdToKey(result.orders)
                setOrders(orders)
                setPagination(prev => {
                    return {
                        ...page,
                        total: result.total
                    }
                })
            }
        )
    }

    useEffect(() => {
        handleTableDataChange(pagination)
        if ('length' in plans && plans.length === 0) {
            Fetch(`${apiUrl}/plans?type=web`, token, (result) => {
                const plans = convertIdToKey(result.plans)
                setPlans(plans)
            })

            Fetch(`${apiUrl}/plans?type=apple`, token, (result) => {
                const plans = convertIdToKey(result.plans)
                plans.forEach(plan => {
                    plan.name = plan.planId
                    plan.enrichedName = plan.name + " (Apple)";
                })
                setApplePlans(plans)
            })

            Fetch(`${apiUrl}/plans?type=android`, token, (result) => {
                const plans = convertIdToKey(result.plans)
                plans.forEach(plan => {
                    plan.name = plan.planId
                    plan.enrichedName = plan.name + " (Android)";
                })
                setAndroidPlans(plans)
            })
        }
    }, [apiUrl, token, filters])

    useEffect(() => {
        let temp_plans = [];
        if (plans && plans.length > 0) {
            temp_plans = [...temp_plans, ...plans];
        }
        if (applePlans && applePlans.length > 0) {
            temp_plans = [...temp_plans, ...applePlans];
        }
        if (androidPlans && applePlans.length > 0) {
            temp_plans = [...temp_plans, ...androidPlans];
        }
        setAllPlans(temp_plans);
    }, [plans, applePlans, androidPlans]);

    const onChangeDate = (date, datestring) => {
        const [start_date, end_date] = [datestring[0], datestring[1]];
        const getStartOfDay = (start_date) => {
            const startOfDay = moment(start_date).startOf("day").format(displayDateFormat);
            return startOfDay;
        };
        const getEndOfDay = (end_date) => {
            const endOfDay = moment(end_date).endOf("day").format(displayDateFormat);
            return endOfDay;
        };
        setFilters({
            ...filters,
            start_date: start_date ? getStartOfDay(start_date) : null,
            end_date: end_date ? getEndOfDay(end_date) : null,
        });
    };

    const customRanges = {
        Today: [moment().startOf("day"), moment().endOf("day")],
        Yesterday: [dayjs().subtract(1, "day"), dayjs().subtract(1, "day")],
        "Last Week": [dayjs().subtract(7, "day"), dayjs()],
    };

    const recheckPayment = (id) => {
        setLoading(true)
        let url = `${apiUrl}/paytm_orders/${id}`;
        try {
            Fetch(url,
                token,
                (result) => {
                    if (result.success) {
                        setOrders(orders.map(order => order.key === id ? { ...order, paid: true } : order))
                    }
                    else {
                        notification.info({
                            message: `PG : ${result.message}`,
                            description: "Check with the PG dashboard for more details"
                        })
                    }
                }
            )
            setLoading(false)
        }
        catch (err) {
            setLoading(false)
            notification.error({
                message: "Something went wrong",
                description: "Check console for more details"
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="manage-orders-wrapper">
            <Row style={{ marginBottom: "20px" }} align={'middle'} >
                <Col span={2} > Paid <Switch checked={filters.paid ? true : false}
                    onChange={(checked) => { setFilters({ ...filters, paid: checked ? true : null }); setPagination({ current: 1, pageSize: 10 }) }} />
                </Col>
                <Col span={3} >
                    <Select style={{ width: "100%" }} placeholder={"Filter by currency"} allowClear
                        onChange={(value) => { setFilters({ ...filters, currency: value }); setPagination({ current: 1, pageSize: 10 }) }}
                    >
                        {['INR', 'EUR', 'USD', 'SGD', 'AED', 'NPR', 'MPR'].map(code =>
                            <Select.Option key={code} value={code}  >{code}</Select.Option>)}
                    </Select>
                </Col>
                <Col span={4}>
                    <Select
                        style={{ width: "100%" }}
                        placeholder={"Filter by Plan"}
                        showSearch
                        value={filters.plan}
                        allowClear
                        onChange={(value) => {
                            setFilters({ ...filters, plan: value, reseller: null });
                            setPagination({ current: 1, pageSize: 10 });
                        }}
                    >
                        {allPlans && allPlans.map((plan, index) => {
                            return (
                                <Select.Option key={index} value={plan.name}>
                                    {plan.enrichedName ? plan.enrichedName : plan.name}
                                </Select.Option>
                            );
                        })}
                    </Select>
                </Col>
                <Col span={7}>
                    <DatePicker.RangePicker
                        ranges={customRanges}
                        onChange={onChangeDate}
                        defaultValue={[
                            dayjs().subtract(0, "month").startOf("month"),
                            dayjs().subtract(0, "month").endOf("month"),
                        ]}
                    />
                </Col>
            </Row>
            <Table
                dataSource={orders}
                pagination={pagination}
                onChange={handleTableDataChange}
            >
                <Column title="ID" dataIndex="key" key="key" align="center" />
                <Column title="User" dataIndex="user" key="language" align="center" />
                <Column title="Date" dataIndex="date" key="date" align="center"
                    render={(_, record) => moment(record.date).format(displayDateFormat)}
                />
                <Column title="Amount" dataIndex="amount" key="amount" align="center" render={(_, record) => `${record.currency} ${record.amount}`} />
                <Column title="Discount Used" dataIndex="discountCode" key="discountCode" align="center" render={code => code === 'None' ? '-' : code} />
                <Column
                    title="Paid"
                    dataIndex="paid"
                    key="paid"
                    align="center"
                    render={paid => paid ? <CheckCircleFilled /> : <CloseCircleOutlined />}
                />
                <Column title="Actions" align="center" render={(_, record) => record.paid ? '-' : <a onClick={() => recheckPayment(record.key)} disabled={loading}>Recheck Status</a>} />
            </Table>
            <CloudDownloadOutlined className="export-btn-only" onClick={() => handleExportClick(apiUrl, token, 'orders')} />
        </div>
    )
}

export default ManageOrders
